const CART_DRAWER_REC_ADDED_MS = 10000;
const CART_DRAWER_DISCOUNT_CACHE_MS = 15000;

window.CartDrawerRecommendations = {
  pending: null,
  resetTimerId: null,
  addonDiscountActive: null,
  addonDiscountCheckPromise: null,

  getAddonDiscountConfig() {
    const el = document.getElementById('CartDrawerAddonDiscountConfig');
    if (!el) return { code: '', percent: 0 };
    try {
      const parsed = JSON.parse(el.textContent);
      return {
        code: String(parsed?.code || '').trim(),
        percent: Number(parsed?.percent || 0),
      };
    } catch {
      return { code: '', percent: 0 };
    }
  },

  getCartRoot() {
    return window.Shopify?.routes?.root || '/';
  },

  clearDiscountCache(code) {
    try {
      if (code) sessionStorage.removeItem(`brydge_addon_discount_${code.toLowerCase()}`);
    } catch {
      /* ignore */
    }
  },

  readDiscountCache(code) {
    try {
      const raw = sessionStorage.getItem(`brydge_addon_discount_${code.toLowerCase()}`);
      if (!raw) return null;
      const cached = JSON.parse(raw);
      if (!cached || Date.now() > cached.expires) return null;
      return Boolean(cached.active);
    } catch {
      return null;
    }
  },

  writeDiscountCache(code, active) {
    try {
      sessionStorage.setItem(
        `brydge_addon_discount_${code.toLowerCase()}`,
        JSON.stringify({ active: Boolean(active), expires: Date.now() + CART_DRAWER_DISCOUNT_CACHE_MS })
      );
    } catch {
      /* ignore */
    }
  },

  async updateCartDiscount(discount) {
    const root = this.getCartRoot();
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ discount: discount || '' }),
    });
    if (!response.ok) {
      let payload = null;
      try {
        payload = await response.json();
      } catch {
        /* ignore */
      }
      return { ok: false, cart: payload };
    }
    return { ok: true, cart: await response.json() };
  },

  async checkDiscountCodeActive(code) {
    let previousDiscount = '';

    try {
      const root = this.getCartRoot();
      const before = await fetch(`${root}cart.js`).then((response) => response.json());
      previousDiscount = (before.discount_codes || [])
        .map((entry) => entry.code)
        .filter(Boolean)
        .join(',');

      // Clear first — leftover session code must not look "active" after Admin disable
      await this.updateCartDiscount('');

      const applied = await this.updateCartDiscount(code);
      let active = false;
      if (applied.ok && applied.cart) {
        const match = (applied.cart.discount_codes || []).find(
          (entry) => String(entry.code || '').toLowerCase() === code.toLowerCase()
        );
        active = Boolean(match);
      }

      await this.updateCartDiscount(previousDiscount);
      return active;
    } catch {
      try {
        await this.updateCartDiscount(previousDiscount);
      } catch {
        /* ignore */
      }
      return false;
    }
  },

  async resolveAddonDiscountActive(force = false) {
    const { percent } = this.getAddonDiscountConfig();
    // Liquid only sets percent when the theme checkbox is ON — trust that; do not probe Admin.
    this.addonDiscountActive = percent > 0;
    this.syncPromoPrices();
    return this.addonDiscountActive;
  },

  syncPromoPrices() {
    document.querySelectorAll('cart-drawer-recommendation').forEach((el) => {
      if (typeof el.refreshPromoPrice === 'function') {
        el.refreshPromoPrice();
      }
    });
  },

  isAddonDiscountActive() {
    return this.addonDiscountActive === true;
  },

  getSlideIndexFromElement(slideEl) {
    if (!slideEl?.parentElement) return -1;

    const slides = slideEl.parentElement.querySelectorAll(':scope > .swiper-slide');
    return Array.from(slides).indexOf(slideEl);
  },

  queueSuccess(variantId, productId, variantSnapshot, slideIndex) {
    if (!variantId) return;

    this.clearResetTimer();
    this.pending = {
      variantId: String(variantId),
      productId: productId != null ? String(productId) : null,
      slideIndex: typeof slideIndex === 'number' && slideIndex >= 0 ? slideIndex : -1,
      option1: variantSnapshot?.option1 ?? null,
      option2: variantSnapshot?.option2 ?? null,
      option3: variantSnapshot?.option3 ?? null,
      expiresAt: Date.now() + CART_DRAWER_REC_ADDED_MS,
    };
    this.scheduleResetTimer();
  },

  clearPending() {
    this.pending = null;
    this.clearResetTimer();
  },

  clearResetTimer() {
    if (this.resetTimerId) {
      clearTimeout(this.resetTimerId);
      this.resetTimerId = null;
    }
  },

  scheduleResetTimer() {
    this.clearResetTimer();
    if (!this.pending) return;

    const remaining = this.pending.expiresAt - Date.now();
    if (remaining <= 0) {
      this.applyAddedState(false);
      this.clearPending();
      return;
    }

    this.resetTimerId = setTimeout(() => {
      this.applyAddedState(false);
      this.clearPending();
    }, remaining);
  },

  scheduleApply(options = {}) {
    this._scheduleOptions = options;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.pending) return;
        this.initSwiperAndFinishUI(0);
      });
    });
  },

  initSwiperAndFinishUI(attempt) {
    if (!this.pending) return;

    if (typeof Swiper === 'undefined' || typeof window.initCartDrawerRecommendationsSwiper !== 'function') {
      if (attempt >= 40) return;
      setTimeout(() => this.initSwiperAndFinishUI(attempt + 1), 100);
      return;
    }

    const drawer = document.querySelector('#CartDrawer');
    const swiperElement = drawer?.querySelector('.cart-drawer__recommendations-swiper');
    if (!swiperElement) {
      if (attempt >= 40) return;
      setTimeout(() => this.initSwiperAndFinishUI(attempt + 1), 100);
      return;
    }

    window.initCartDrawerRecommendationsSwiper(() => {
      this.finishRecommendationUI(this._scheduleOptions || {});
    });
  },

  finishRecommendationUI(options = {}) {
    this.slideToPendingProduct(0, () => {
      this.applyAddedState(true);
      this.scheduleResetTimer();

      if (options.scrollIntoView) {
        this.scrollRecommendationsIntoView();
      }
    });
  },

  getPendingSlideIndex(swiper) {
    if (!this.pending) return -1;

    if (this.pending.slideIndex >= 0) {
      return this.pending.slideIndex;
    }

    if (swiper?.slides?.length && this.pending.productId) {
      const index = Array.from(swiper.slides).findIndex(
        (slide) => String(slide.dataset?.productId) === this.pending.productId
      );
      if (index >= 0) return index;
    }

    if (!this.pending.productId) return -1;

    const slides = document.querySelectorAll(
      '#CartDrawer .cart-drawer__recommendations-swiper .swiper-slide[data-product-id]'
    );

    let targetIndex = -1;
    slides.forEach((slide, index) => {
      if (String(slide.dataset.productId) === this.pending.productId) {
        targetIndex = index;
      }
    });

    return targetIndex;
  },

  slideToPendingProduct(attempt = 0, onComplete) {
    const swiperElement = document.querySelector('#CartDrawer .cart-drawer__recommendations-swiper');
    const swiper = swiperElement?.swiper;
    const slideIndex = this.getPendingSlideIndex(swiper);

    if (!swiper || slideIndex < 0) {
      if (attempt < 40) {
        setTimeout(() => this.slideToPendingProduct(attempt + 1, onComplete), 80);
      } else if (typeof onComplete === 'function') {
        onComplete();
      }
      return false;
    }

    swiper.update();
    swiper.slideTo(slideIndex, 0);

    requestAnimationFrame(() => {
      swiper.update();
      swiper.slideTo(slideIndex, 0);

      if (typeof onComplete === 'function') {
        onComplete();
      }
    });

    return true;
  },

  scrollRecommendationsIntoView() {
    const scrollContainer = document.querySelector('cart-drawer-items');
    const recommendations = document.querySelector('.cart-drawer__recommendations');
    if (!scrollContainer || !recommendations) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const targetRect = recommendations.getBoundingClientRect();
    const offset = targetRect.top - containerRect.top + scrollContainer.scrollTop - 12;

    scrollContainer.scrollTo({
      top: Math.max(0, offset),
      behavior: 'smooth',
    });
  },

  matchesPending(el) {
    if (!this.pending || !el) return false;

    const productId = el.closest('[data-product-id]')?.dataset?.productId;
    if (this.pending.productId && productId && String(productId) === this.pending.productId) {
      return true;
    }

    const variantInput = el.querySelector('[data-rec-variant-id]');
    return !!variantInput && String(variantInput.value) === this.pending.variantId;
  },

  applyAddedState(isAdded) {
    document.querySelectorAll('cart-drawer-recommendation').forEach((el) => {
      if (isAdded) {
        if (!this.matchesPending(el)) return;

        if (this.pending?.variantId) {
          el.selectVariantById(this.pending.variantId, {
            preserveAdded: true,
            fallbackOptions: {
              1: this.pending.option1,
              2: this.pending.option2,
              3: this.pending.option3,
            },
          });
        }
        el.setAddedState(true);
      } else {
        el.setAddedState(false);
      }
    });
  },
};

if (!customElements.get('cart-drawer-recommendation')) {
  customElements.define(
    'cart-drawer-recommendation',
    class CartDrawerRecommendation extends HTMLElement {
      connectedCallback() {
        this.variants = this.parseJson('[data-rec-product-variants]');
        this.optionNames = this.parseJson('[data-rec-product-options]');
        this.variantInput = this.querySelector('[data-rec-variant-id]');
        this.priceCurrent = this.querySelector('[data-rec-price-current]');
        this.priceCompare = this.querySelector('[data-rec-price-compare]');
        this.priceDiscount = this.querySelector('[data-rec-price-discount]');
        this.priceWrap = this.querySelector('[data-rec-price]');
        this.addonDiscountPercent = Number(this.priceWrap?.dataset?.addonDiscount || 0);
        this.recImage = this.querySelector('[data-rec-image]');
        this.submitButton = this.querySelector('[data-rec-submit]');

        // Checkbox-gated: if Liquid passed a %, treat promo as on immediately
        if (this.addonDiscountPercent > 0 && window.CartDrawerRecommendations) {
          window.CartDrawerRecommendations.addonDiscountActive = true;
        }

        this.querySelectorAll('input[type="radio"][data-option-position]').forEach((input) => {
          input.addEventListener('change', (event) => {
            event.stopPropagation();
            this.onOptionChange();
          });
        });

        const pending = window.CartDrawerRecommendations?.pending;
        if (pending && window.CartDrawerRecommendations.matchesPending(this)) {
          this.selectVariantById(pending.variantId, {
            preserveAdded: true,
            fallbackOptions: {
              1: pending.option1,
              2: pending.option2,
              3: pending.option3,
            },
          });
          this.setAddedState(true);
        } else {
          this.updateVariantState();
        }

        if (this.addonDiscountPercent > 0 && window.CartDrawerRecommendations) {
          window.CartDrawerRecommendations.resolveAddonDiscountActive(true);
        }
      }

      refreshPromoPrice() {
        const variant = this.getVariantById(this.variantInput?.value) || this.findVariant(this.getSelectedOptions());
        if (variant) this.updateVariantPresentation(variant);
      }

      parseJson(selector) {
        const el = this.querySelector(selector);
        if (!el) return [];
        try {
          return JSON.parse(el.textContent);
        } catch {
          return [];
        }
      }

      getVariantById(variantId) {
        return this.variants.find((variant) => String(variant.id) === String(variantId));
      }

      selectVariantById(variantId, options = {}) {
        let variant = this.getVariantById(variantId);

        const fallbackSelected = {};
        if (options.fallbackOptions) {
          if (options.fallbackOptions[1]) fallbackSelected[1] = options.fallbackOptions[1];
          if (options.fallbackOptions[2]) fallbackSelected[2] = options.fallbackOptions[2];
          if (options.fallbackOptions[3]) fallbackSelected[3] = options.fallbackOptions[3];
        }

        if (!variant && Object.keys(fallbackSelected).length > 0) {
          variant = this.findVariant(fallbackSelected);
        }

        if (!variant) {
          if (!options.preserveAdded) this.updateVariantState();
          return false;
        }

        for (let position = 1; position <= 3; position += 1) {
          const optionValue = variant[`option${position}`];
          if (!optionValue) continue;

          this.querySelectorAll(`input[type="radio"][data-option-position="${position}"]`).forEach((input) => {
            input.checked = input.value === optionValue;
          });
        }

        if (this.variantInput) {
          this.variantInput.value = variant.id;
          if (variant.available) {
            this.variantInput.removeAttribute('disabled');
          } else {
            this.variantInput.setAttribute('disabled', 'disabled');
          }
        }

        this.updateAvailabilityStates();
        this.updateSelectedLabels();
        this.updateVariantPresentation(variant);

        return true;
      }

      setAddedState(isAdded) {
        const button = this.submitButton;
        if (!button) return;

        const defaultLabel = button.querySelector('[data-rec-submit-default]');
        const addedLabel = button.querySelector('[data-rec-submit-added]');

        if (isAdded) {
          button.classList.add('is-added');
          button.setAttribute('aria-disabled', 'true');
          defaultLabel?.setAttribute('hidden', '');
          addedLabel?.removeAttribute('hidden');
          return;
        }

        button.classList.remove('is-added');
        button.removeAttribute('aria-disabled');
        defaultLabel?.removeAttribute('hidden');
        addedLabel?.setAttribute('hidden', '');

        const variant = this.getVariantById(this.variantInput?.value);
        if (variant?.available) {
          button.removeAttribute('disabled');
        } else {
          button.setAttribute('disabled', 'disabled');
        }
      }

      getSelectedOptions() {
        const selected = {};

        this.querySelectorAll('input[type="radio"][data-option-position]:checked').forEach((input) => {
          selected[Number(input.dataset.optionPosition)] = input.value;
        });

        if (Object.keys(selected).length > 0) return selected;

        const currentVariant = this.getVariantById(this.variantInput?.value);
        if (currentVariant) {
          if (currentVariant.option1) selected[1] = currentVariant.option1;
          if (currentVariant.option2) selected[2] = currentVariant.option2;
          if (currentVariant.option3) selected[3] = currentVariant.option3;
        }

        return selected;
      }

      findVariant(selected) {
        const matches = this.variants.filter((variant) => {
          if (selected[1] && variant.option1 !== selected[1]) return false;
          if (selected[2] && variant.option2 !== selected[2]) return false;
          if (selected[3] && variant.option3 !== selected[3]) return false;
          return true;
        });

        return matches.find((variant) => variant.available) || matches[0];
      }

      isOptionValueAvailable(optionPosition, value, selected) {
        return this.variants.some((variant) => {
          if (!variant.available) return false;
          if (optionPosition === 1 && variant.option1 !== value) return false;
          if (optionPosition === 2) {
            if (variant.option2 !== value) return false;
            if (selected[1] && variant.option1 !== selected[1]) return false;
          }
          if (optionPosition === 3) {
            if (variant.option3 !== value) return false;
            if (selected[1] && variant.option1 !== selected[1]) return false;
            if (selected[2] && variant.option2 !== selected[2]) return false;
          }
          return true;
        });
      }

      updateAvailabilityStates() {
        const selected = this.getSelectedOptions();
        this.querySelectorAll('input[type="radio"][data-option-position]').forEach((input) => {
          const position = Number(input.dataset.optionPosition);
          const available = this.isOptionValueAvailable(position, input.value, selected);
          input.classList.toggle('disabled', !available);

          const label = this.querySelector(`label[for="${input.id}"]`);
          if (label) label.classList.toggle('is-unavailable', !available);
        });
      }

      updateSelectedLabels() {
        this.querySelectorAll('input[type="radio"][data-option-position]:checked').forEach((input) => {
          const label = this.querySelector(`[data-rec-selected-label="${input.dataset.optionPosition}"]`);
          if (label) label.textContent = input.value;
        });
      }

      formatMoney(cents) {
        const currency = window.cartDrawerCurrency || 'USD';
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: currency,
        }).format(cents / 100);
      }

      updateVariantPresentation(variant) {
        if (!variant) return;

        const promoActive =
          this.addonDiscountPercent > 0 && window.CartDrawerRecommendations?.isAddonDiscountActive();
        const saleCents = promoActive
          ? Math.round((variant.price * (100 - this.addonDiscountPercent)) / 100)
          : variant.price;

        if (this.priceWrap) {
          this.priceWrap.classList.toggle('cart-drawer__recommendation-price--promo', promoActive);
        }

        if (this.priceCurrent) {
          this.priceCurrent.textContent = this.formatMoney(saleCents);
        }

        if (this.priceCompare) {
          if (promoActive) {
            this.priceCompare.textContent = this.formatMoney(variant.price);
            this.priceCompare.removeAttribute('hidden');
          } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            this.priceCompare.textContent = this.formatMoney(variant.compare_at_price);
            this.priceCompare.removeAttribute('hidden');
          } else {
            this.priceCompare.setAttribute('hidden', 'hidden');
          }
        }

        if (this.priceDiscount) {
          if (promoActive) {
            this.priceDiscount.textContent = `-${this.addonDiscountPercent}%`;
            this.priceDiscount.removeAttribute('hidden');
          } else {
            this.priceDiscount.setAttribute('hidden', 'hidden');
          }
        }

        if (this.recImage && variant.featured_image) {
          const src = variant.featured_image.src || variant.featured_image.url;
          if (src) {
            const width = this.recImage.getAttribute('width') || '420';
            const normalizedSrc = src.startsWith('//') ? `https:${src}` : src;
            this.recImage.src = normalizedSrc.includes('?')
              ? `${normalizedSrc}&width=${width}`
              : `${normalizedSrc}?width=${width}`;
            if (variant.featured_image.alt) this.recImage.alt = variant.featured_image.alt;
          }
        }

        if (this.submitButton && !this.submitButton.classList.contains('is-added')) {
          if (variant.available) {
            this.submitButton.removeAttribute('disabled');
          } else {
            this.submitButton.setAttribute('disabled', 'disabled');
          }
        }
      }

      updateVariantState() {
        const selected = this.getSelectedOptions();
        const variant = this.findVariant(selected);

        this.updateAvailabilityStates();
        this.updateSelectedLabels();

        if (!variant || !this.variantInput) return;

        const previousVariantId = this.variantInput.value;
        this.variantInput.value = variant.id;

        if (variant.available) {
          this.variantInput.removeAttribute('disabled');
        } else {
          this.variantInput.setAttribute('disabled', 'disabled');
        }

        if (
          this.submitButton?.classList.contains('is-added') &&
          String(previousVariantId) !== String(variant.id)
        ) {
          this.setAddedState(false);
          if (window.CartDrawerRecommendations?.matchesPending(this)) {
            window.CartDrawerRecommendations.clearPending();
          }
        }

        this.updateVariantPresentation(variant);
      }

      onOptionChange() {
        this.updateVariantState();
      }
    }
  );
}
